import React, { FC, useCallback, useState, useMemo } from "react";
import { Button, IconButton, Tooltip, Switch } from "@xapp/components";
import { Paper } from "@mui/material";
import { AlertSubscription, AlertType } from "generated/graphql";
import { AlertSubscriptionEditor } from "./AlertSubscriptionEditor";
import { formatAlertSubscriptionFilters, getAppName } from "../utils/formatters";
import { SubscribableApp } from "../types";

export interface AlertSubscriptionListProps {
    subscriptions: AlertSubscription[];
    subscribableApps: SubscribableApp[];
    onCreate: (subscription: Partial<AlertSubscription>) => void;
    onUpdate: (subscriptionId: string, subscription: Partial<AlertSubscription>) => void;
    onDelete: (subscriptionId: string) => void;
}

export const AlertSubscriptionList: FC<AlertSubscriptionListProps> = (props) => {
    const { subscriptions, subscribableApps, onCreate, onUpdate, onDelete } = props;

    const [editingSubscription, setEditingSubscription] = useState<AlertSubscription | null>(null);
    const [editorActive, setEditorActive] = useState(false);
    const [editorAlertType, setEditorAlertType] = useState<AlertType | null>(null);

    const handleAdd = useCallback((alertType: AlertType) => {
        setEditorAlertType(alertType);
        setEditingSubscription(null);
        setEditorActive(true);
    }, []);

    const handleEdit = useCallback((subscription: AlertSubscription) => {
        setEditingSubscription(subscription);
        setEditorAlertType(subscription.alertType);
        setEditorActive(true);
    }, []);

    const handleDelete = useCallback(
        (subscriptionId: string) => {
            if (confirm("Are you sure you want to delete this subscription?")) {
                onDelete(subscriptionId);
            }
        },
        [onDelete]
    );

    const handleToggleEnabled = useCallback(
        (subscriptionId: string, enabled: boolean) => {
            onUpdate(subscriptionId, { enabled });
        },
        [onUpdate]
    );

    const handleSave = useCallback(
        (subscription: Partial<AlertSubscription>) => {
            if (editingSubscription) {
                onUpdate(editingSubscription.subscriptionId, subscription);
            } else {
                onCreate(subscription);
            }
            setEditorActive(false);
            setEditingSubscription(null);
            setEditorAlertType(null);
        },
        [editingSubscription, onCreate, onUpdate]
    );

    const handleDismiss = useCallback(() => {
        setEditorActive(false);
        setEditingSubscription(null);
        setEditorAlertType(null);
    }, []);

    const getAppNameMemo = useMemo(() => (appId: string) => getAppName(appId, subscribableApps), [subscribableApps]);

    const renderSubscriptionsByType = (alertType: AlertType, title: string) => {
        const typeSubscriptions = subscriptions.filter((s) => s.alertType === alertType);

        return (
            <div style={{ marginBottom: "24px" }}>
                <h4>{title}</h4>
                {typeSubscriptions.length === 0 ? (
                    <p>No subscriptions yet.</p>
                ) : (
                    typeSubscriptions.map((sub) => (
                        <Paper key={sub.subscriptionId} sx={{ marginBottom: "12px", padding: "16px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div style={{ flex: 1 }}>
                                    <p><strong>Apps:</strong> {sub.appIds.map(getAppNameMemo).join(", ")}</p>
                                    <p><strong>Channel:</strong> {sub.channel}</p>
                                    <p><strong>Filters:</strong> {formatAlertSubscriptionFilters(sub)}</p>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <Switch
                                        checked={sub.enabled}
                                        onChange={(checked) => handleToggleEnabled(sub.subscriptionId, checked)}
                                    />
                                    <Tooltip content="Edit">
                                        <IconButton iconName="edit" onClick={() => handleEdit(sub)} />
                                    </Tooltip>
                                    <Tooltip content="Delete">
                                        <IconButton iconName="delete" onClick={() => handleDelete(sub.subscriptionId)} />
                                    </Tooltip>
                                </div>
                            </div>
                        </Paper>
                    ))
                )}
                <Button onClick={() => handleAdd(alertType)}>Add {title}</Button>
            </div>
        );
    };

    return (
        <div>
            {renderSubscriptionsByType(AlertType.LeadCaptured, "Lead Captured Alerts")}
            {renderSubscriptionsByType(AlertType.LeadAlert, "Opportunity Alerts")}
            {renderSubscriptionsByType(AlertType.ChatStarted, "Chat Started Alerts")}

            <AlertSubscriptionEditor
                subscription={editingSubscription}
                alertType={editorAlertType}
                subscribableApps={subscribableApps}
                active={editorActive}
                onSave={handleSave}
                onDismiss={handleDismiss}
            />
        </div>
    );
};
